import { useCallback, useEffect, useRef, useState } from 'react';

import { RiCloseLine, RiSearchLine } from '@remixicon/react';
import classNames from 'classnames';

import {
  ButtonIcon,
  FormInput,
  FormInputIcon,
  Icon,
} from '@spotify-internal/encore-web';

import { useDebounced } from '../../hooks/useDebounced';
import { useFilterShortcut } from '../../hooks/useFilterShortcut';
import { useFilters } from './provider';

import styles from './FilterSearch.module.css';

interface FilterSearchProps {
  searchCollapsed?: boolean;
  debounceTime?: number;
  placeholder?: string;
}

export const FilterSearch = ({
  searchCollapsed = false,
  debounceTime = 300,
  placeholder = 'Filter...',
}: FilterSearchProps) => {
  const { searchQuery, setQuery } = useFilters();
  const containerRef = useRef<HTMLDivElement>(null);
  const [filterShortcutEnabled] = useFilterShortcut();
  const [expanded, setExpanded] = useState(false);

  const [displayValue, handleChangeDebounced, handleChangeImmediately] =
    useDebounced<string>(searchQuery, setQuery, debounceTime);

  const isCollapsed = searchCollapsed && !expanded && !displayValue;

  const handleKeyDown = useCallback(
    (event: {
      key: string;
      metaKey: boolean;
      shiftKey: boolean;
      preventDefault: () => void;
    }) => {
      if (event.key !== 'f' || !event.metaKey || event.shiftKey) return;
      if (!filterShortcutEnabled) return;

      if (searchCollapsed && !expanded) {
        event.preventDefault();
        setExpanded(true);
        return;
      }

      const input = containerRef.current?.querySelector<HTMLElement>('input');
      if (!input) return;

      if (document.activeElement === input) return;

      event.preventDefault();
      input.focus();
    },
    [filterShortcutEnabled, searchCollapsed, expanded],
  );

  useEffect(() => {
    const listener = (event: KeyboardEvent) => handleKeyDown(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [handleKeyDown]);

  if (isCollapsed) {
    return (
      <ButtonIcon
        variant="secondary"
        size="small"
        aria-label="search"
        className={styles.collapsedButton}
        onClick={() => setExpanded(true)}
      >
        <RiSearchLine size={18} />
      </ButtonIcon>
    );
  }

  return (
    <div
      ref={containerRef}
      className={classNames(styles.root, {
        [styles.rootCollapsed]: searchCollapsed,
      })}
    >
      <FormInputIcon
        className={styles.field}
        iconLeading={
          <Icon as={RiSearchLine} size="small" semanticColor="textSubdued" />
        }
        iconTrailing={
          displayValue ? (
            <ButtonIcon
              className={styles.clearButton}
              variant="tertiary"
              size="small"
              aria-label="Clear search"
              onClick={() => handleChangeImmediately('')}
            >
              <RiCloseLine size={16} />
            </ButtonIcon>
          ) : undefined
        }
      >
        <FormInput
          className={styles.input}
          size="small"
          type="search"
          name="filter-bar"
          placeholder={placeholder}
          value={displayValue}
          autoFocus={expanded}
          onChange={e => handleChangeDebounced(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (searchCollapsed && !displayValue) setExpanded(false);
          }}
          aria-label="search"
        />
      </FormInputIcon>
    </div>
  );
};
